import { useEffect, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiAlert,
  MiButton,
  MiDetailPageHeader,
  MiIcon,
  MiMenu,
} from "@miplatform/components";
import {
  BUTTON_VARIANTS,
  HTTP_STATUSES,
  PERMISSIONS,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { AreaDesignation } from "@miplatform/types";
import { mdiChevronDown, mdiTextBoxSearchOutline } from "@mdi/js";
import { usePermissions } from "@mibuildweb/hooks";
import { Box, Typography } from "@mui/material";
import { StatusChip } from "../../components/StatusChip/StatusChip";
import { StepperView } from "../../components/StepperView/StepperView";
import { ApproveModal } from "./ApproveModal/ApproveModal";
import { AreaTemplateModal } from "./AreaTemplateModal/AreaTemplateModal";
import { BidDetailsForm } from "./BidDetailsForm/BidDetailsForm";
import { ConfigureBid } from "./ConfigureBid/ConfigureBid";
import { ConfigureOptions } from "./ConfigureOptions/ConfigureOptions";
import { CreateBidProvider, useCreateBidContext } from "./CreateBidContext";
import { PricingAdjustments } from "./PricingAdjustments/PricingAdjustments";
import { Review } from "./Review/Review";
import { SelectRequirements } from "./SelectRequirements/SelectRequirements";
import { StartingPoint } from "./StartingPoint/StartingPoint";
import { BID_STATUS, START_OPTION, STEP, STEPS } from "./constants";

function CreateBidContent() {
  const [openAreaTemplateModal, setOpenAreaTemplateModal] = useState(false);
  const [openApproveModal, setOpenApproveModal] = useState(false);
  const [isSubmittingStep, setIsSubmittingStep] = useState(false);

  const { params, navigate } = useRouter();
  const { bidId } = params;
  const {
    isLoading,
    form,
    activeStep,
    fetchBidDetails,
    setActiveStep,
    handleUpdateBid,
    handleUpdateBidStatus,
    handleUpdatePlanRequirements,
    communities,
    getCommunities,
    accounts,
    getAccounts,
    isConfigStepValid,
    startOption,
    openDocumentViewer,
    setOpenDocumentViewer,
    floors,
    getFloors,
    getAreaTypes,
    getCategoryFilters,
    getAccountDetails,
    getSeriesGroups,
  } = useCreateBidContext();
  const { classes, theme } = useStyles({ openDocumentViewer });
  const {
    formState: { isValid, isDirty },
    watch,
    getValues,
  } = form;
  const { hasPermission } = usePermissions();
  const canReadPricing = hasPermission(PERMISSIONS.PRICING.READ);

  const {
    name,
    communityId,
    builderId,
    additionalFeatures,
    planAreas,
    documents,
    planStatus,
  } = watch();

  const getAllData = async () => {
    await Promise.all([
      fetchBidDetails(bidId),
      getCommunities(),
      getAccounts(),
      getFloors(),
      getAreaTypes(),
      getCategoryFilters(),
    ]);
  };

  useEffect(() => {
    getAllData();
  }, []);

  useEffect(() => {
    if (builderId) {
      getAccountDetails(builderId);
      getSeriesGroups({
        filters: { accountId: { type: "guid", value: builderId } },
      });
    }
  }, [builderId]);

  const shouldSkipStartStep =
    planAreas.length > 0 &&
    activeStep === STEP.START &&
    startOption !== START_OPTION.AREA_TEMPLATES &&
    startOption !== START_OPTION.COPY;

  useEffect(() => {
    if (shouldSkipStartStep) {
      setActiveStep(STEP.DETAILS);
    }
  }, [planAreas.length, activeStep, startOption]);

  useEffect(() => {
    setOpenDocumentViewer(false);
  }, [activeStep]);

  const isStartOptionValid = () => {
    if (startOption === START_OPTION.SCRATCH) {
      return false;
    }
    if (startOption === START_OPTION.AREA_TEMPLATES && planAreas.length) {
      return false;
    }
    return true;
  };

  const ACTIVE_STEP_CONFIG = useMemo(
    () => ({
      [STEP.START]: {
        disabled: isStartOptionValid(),
      },
      [STEP.DETAILS]: {
        disabled: !isValid,
        hideBackButton: planAreas.length > 0,
      },
      [STEP.CONFIGURE]: {
        disabled: !isConfigStepValid,
      },
      [STEP.OPTIONS]: {
        disabled: !isValid,
      },
      [STEP.REQUIREMENTS]: {
        disabled: !isValid,
      },
    }),
    [isValid, isConfigStepValid, startOption, planAreas]
  );

  const bidApproved = planStatus === BID_STATUS.APPROVED;
  const selectedConfig = ACTIVE_STEP_CONFIG[activeStep] || {};
  const PRICING_STEPS = [STEP.PRICING_ADJUSTMENTS, STEP.REVIEW];
  const visibleSteps = STEPS.filter(
    (s) => s.visible && (canReadPricing || !PRICING_STEPS.includes(s.value))
  );

  // guard against ending up on the pricing step without permission
  useEffect(() => {
    if (
      (activeStep === STEP.PRICING_ADJUSTMENTS || activeStep === STEP.REVIEW) &&
      !canReadPricing
    ) {
      setActiveStep(STEP.REQUIREMENTS);
    }
  }, [activeStep, canReadPricing]);

  const customerName = accounts.find((a) => a.id === builderId)?.name;
  const communityName = communities.find((c) => c.id === communityId)?.name;

  // total bath counts in all floors
  const bath = planAreas.filter(
    (a) => a.areaDesignation === AreaDesignation.Bath
  );
  const halfBath = planAreas.filter(
    (a) => a.areaDesignation === AreaDesignation.HalfBath
  );
  const hasBaths = bath.length > 0 || halfBath.length > 0;
  const numberOfBaths = [bath.length, ...halfBath.map(() => ".5")].join("");
  // floors
  const numberOfFloors = [...new Set(planAreas.map((a) => a.floorId))];
  const firstSelectedFloor = floors.find((floor) =>
    numberOfFloors.includes(floor.id)
  );
  const firstFloorId = firstSelectedFloor?.id ?? numberOfFloors[0];
  // total bath counts above 1st floor
  const upBath = bath.filter((a) => a.floorId !== firstFloorId);
  const upHalfBath = halfBath.filter((a) => a.floorId !== firstFloorId);
  const hasUpBaths = upBath.length > 0 || upHalfBath.length > 0;
  const numberOfUps = [upBath.length, ...upHalfBath.map(() => ".5")].join("");

  const headerDescription = [
    hasBaths && `${numberOfBaths} Bath${numberOfBaths > "1" ? "s" : ""}`,
    numberOfFloors.length && `${numberOfFloors.length} Story`,
    hasUpBaths && `${numberOfUps} Up`,
    additionalFeatures,
  ]
    .filter(Boolean)
    .join(" - ");

  const handleSaveBid = async () => {
    const data = getValues();
    const successText = `${data.name} was updated successfully.`;
    const success = await handleUpdateBid(data, successText);
    if (!success) return false;
    if (activeStep !== STEP.REQUIREMENTS) return true;
    const requirementsResponse = await handleUpdatePlanRequirements(
      data.id,
      data.requirementIds
    );
    return requirementsResponse.status === HTTP_STATUSES.OK;
  };

  const goBack = () => navigate(-1);

  const handleStep = async (step: number) => {
    const data = getValues();
    if (isSubmittingStep) return;
    setIsSubmittingStep(true);
    try {
      const isMovingForward = step > activeStep;
      // save bid if there are changes
      if (activeStep !== STEP.START && isDirty) {
        const success = await handleSaveBid();
        if (!success) return;
      }
      // leaving requirements step forward
      if (activeStep === STEP.REQUIREMENTS && isMovingForward) {
        // move bid to pricing status only if it isn't already pricing or approved
        if (planStatus !== BID_STATUS.PRICING && !bidApproved) {
          const success = await handleUpdateBidStatus(data, BID_STATUS.PRICING);
          if (!success) return;
        }
        // no pricing perm: submit and leave
        if (!canReadPricing) {
          goBack();
          return;
        }
        // has pricing perm: fall through to setActiveStep(step) -> pricing adjustments
      }
      // end of review step (forward only): finish or open approve modal
      if (activeStep === STEP.REVIEW && isMovingForward) {
        if (bidApproved) {
          goBack();
        } else {
          setOpenApproveModal(true);
        }
        return;
      }
      setActiveStep(step);
    } finally {
      setIsSubmittingStep(false);
    }
  };

  const getNextLabel = () => {
    if (activeStep === STEP.REVIEW && bidApproved) return "Done";
    if (activeStep === STEP.REVIEW) return "Approve";
    if (activeStep === STEP.REQUIREMENTS && !canReadPricing) return "Submit";
    return "Next";
  };

  return (
    <>
      <MiDetailPageHeader
        title={
          <>
            Create Bid
            {(name || communityId || builderId) && (
              <span className={classes.titleSpan}>
                :{" "}
                {[customerName || "Customer", communityName, name || "Bid Name"]
                  .filter(Boolean)
                  .join(" | ")}
              </span>
            )}{" "}
            <StatusChip status={planStatus} />
          </>
        }
        actionComponent={
          activeStep > STEP.START && (
            <Box className={classes.actionBox}>
              <Typography className={classes.actionText} variant="h5">
                {headerDescription}
              </Typography>
              <Box className={classes.headerActionsRow}>
                {documents.length > 0 && (
                  <MiButton
                    startIcon={{ path: mdiTextBoxSearchOutline }}
                    variant={BUTTON_VARIANTS.OUTLINED}
                    className={classes.docViewerToggleButton}
                    onClick={() => {
                      if (openDocumentViewer) {
                        setOpenDocumentViewer(false);
                      } else {
                        setOpenDocumentViewer(true);
                      }
                    }}
                  />
                )}
                <Box className={classes.saveButtonRow}>
                  <MiButton
                    title="Save"
                    variant={BUTTON_VARIANTS.CONTAINED}
                    disabled={!isDirty || !isValid}
                    onClick={handleSaveBid}
                    className={classes.saveButton}
                  />
                  <Box className={classes.menuBox}>
                    <MiMenu
                      data={[
                        {
                          label: "Save Area Template",
                          action: () => setOpenAreaTemplateModal(true),
                          disabled:
                            planAreas.length === 0 ||
                            activeStep === STEP.DETAILS,
                        },
                        {
                          label: "Save & Close",
                          disabled: !isDirty || !isValid,
                          action: async () => {
                            await handleSaveBid();
                            goBack();
                          },
                        },
                      ]}
                      optionalElement={
                        <MiIcon
                          path={mdiChevronDown}
                          color={theme.palette.common.white}
                        />
                      }
                      optionalElementStyle={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          )
        }
        actionContainerStyle={{
          display: "flex",
          flex: 2,
          justifyContent: "flex-end",
        }}
        style={{
          backgroundColor: theme.palette.common.white,
        }}
        loading={isLoading}
        customOnBack={goBack}
      />
      {bidApproved && (
        <MiAlert
          leftIcon="mdiLockOutline"
          hideCloseButton
          variant="warning"
          sx={{ height: 30 }}
          iconSize={0.8}
          title="This bid is read-only because it was approved. To make changes, create an update request."
        />
      )}
      <StepperView
        steps={visibleSteps}
        activeStep={activeStep}
        setActiveStep={handleStep}
        disabled={selectedConfig.disabled || isSubmittingStep}
        disableNavigation
        stepOffset={1}
        hideBackButton={selectedConfig.hideBackButton}
        nextLabel={getNextLabel()}
      >
        {activeStep === STEP.START && <StartingPoint />}
        {activeStep === STEP.DETAILS && <BidDetailsForm />}
        {activeStep === STEP.CONFIGURE && <ConfigureBid />}
        {activeStep === STEP.OPTIONS && <ConfigureOptions />}
        {activeStep === STEP.REQUIREMENTS && <SelectRequirements />}
        {activeStep === STEP.PRICING_ADJUSTMENTS && canReadPricing && (
          <PricingAdjustments />
        )}
        {activeStep === STEP.REVIEW && canReadPricing && <Review />}
      </StepperView>
      <AreaTemplateModal
        open={openAreaTemplateModal}
        onClose={() => setOpenAreaTemplateModal(false)}
      />
      <ApproveModal
        open={openApproveModal}
        closeModal={() => setOpenApproveModal(false)}
        goBack={goBack}
      />
    </>
  );
}

export function CreateBid() {
  return (
    <CreateBidProvider>
      <CreateBidContent />
    </CreateBidProvider>
  );
}

const useStyles = makeStyles<{ openDocumentViewer: boolean }>()(
  (theme, { openDocumentViewer }) => ({
    titleSpan: {
      color: theme.palette.primary.main,
    },
    actionBox: {
      display: "flex",
      gap: theme.spacing(3),
      alignItems: "center",
    },
    actionText: {
      color: theme.palette.primary.main,
      fontWeight: theme.fontWeight.semiBold,
    },
    headerActionsRow: {
      display: "flex",
      gap: theme.spacing(2),
    },
    docViewerToggleButton: {
      backgroundColor: openDocumentViewer
        ? theme.palette.primary.light
        : undefined,
    },
    saveButtonRow: {
      display: "flex",
    },
    saveButton: {
      borderRadius: "4px 0px 0px 4px",
    },
    menuBox: {
      backgroundColor: theme.palette.primary.main,
      display: "flex",
      alignItems: "center",
      borderRadius: "0px 4px 4px 0px",
      borderLeft: `1px solid ${theme.palette.grey.accent}`,
      cursor: "pointer",
    },
  })
);
